/**
 * Named GS1 Application Identifier constants for shared use across services.
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3–4.10; GS1 General Specifications Release 26.0 §3.2.
 */
export const GS1_AI = {
    SSCC: '00',
    GTIN: '01',
    CONTENT_GTIN: '02',
    BATCH_LOT: '10',
    PROD_DATE: '11',
    DUE_DATE: '12',
    PACK_DATE: '13',
    BEST_BEFORE: '15',
    SELL_BY: '16',
    EXPIRY_DATE: '17',
    VARIANT: '20',
    SERIAL: '21',
    CPV: '22',
    TPX: '235',
    GDTI: '253',
    GLN_EXT: '254',
    GCN: '255',
    NET_WEIGHT_KG3: '3103',
    PRICE: '3922',
    GINC: '401',
    GSIN: '402',
    PHYSICAL_GLN: '414',
    PAY_TO_GLN: '415',
    PARTY_GLN: '417',
    SHIP_TO_COMP: '4300',
    EXPIRY_DATE_TIME: '7003',
    UIC_EXT: '7040',
    PROTOCOL: '7240',
    EXTENDED_ATTR: '7259',
    GRAI: '8003',
    GIAI: '8004',
    ITIP: '8006',
    PROD_TIME: '8008',
    CPID: '8010',
    CPID_SERIAL: '8011',
    GMN: '8013',
    GSRN_PROVIDER: '8017',
    GSRN_RECIPIENT: '8018',
    SRIN: '8019',
    PAYMENT_REF: '8020',
    DIGSIG: '8030',
};
/**
 * All 16 GS1 Primary Identification Key Application Identifier forms (across the 12 GS1 identification keys)
 * (GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23 & §4.5, pp23–24).
 */
export const GS1_PRIMARY_KEY_AIS = [
    '01',
    '8006',
    '8013',
    '8010',
    '414',
    '415',
    '417',
    '8017',
    '8018',
    '255',
    '00',
    '253',
    '401',
    '402',
    '8003',
    '8004',
];
export const PRIMARY_KEY_SET = new Set(GS1_PRIMARY_KEY_AIS);
export const QUALIFIER_ORDER_BY_PRIMARY = {
    '01': ['22', '10', '21', '235'],
    '8006': ['22', '10', '21'],
    '8013': [],
    '8010': ['8011'],
    '414': ['254', '7040'],
    '415': ['8020'],
    '417': ['7040'],
    '8017': ['8019'],
    '8018': ['8019'],
    '255': [],
    '00': [],
    '253': [],
    '401': [],
    '402': [],
    '8003': [],
    '8004': ['7040'],
};
/**
 * Key qualifiers that MUST appear in the URI path, never in the query string
 * (except '10' which is permitted in the query string ONLY with SSCC '00' + Content '02' per §4.10 p33).
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.4, p23 & §4.8–4.9, pp25–26.
 */
export const PATH_ONLY_QUALIFIERS = new Set([
    '22',
    '21',
    '235',
    '8011',
    '254',
    '7040',
    '8020',
    '8019',
]);
/**
 * Parses a Firestore identifier document ID in either `{ai}_{primaryKey}`
 * or `{workspaceId}_{ai}_{primaryKey}` format into its constituent GS1 AI and primary key.
 */
export function parseIdentifierDocId(docId) {
    const directMatch = /^(01|8006|8013|8010|414|415|417|8017|8018|255|00|253|401|402|8003|8004)_([^_]+)$/.exec(docId);
    if (directMatch !== null) {
        const ai = directMatch[1];
        const primaryKey = directMatch[2];
        if (ai !== undefined && primaryKey !== undefined && primaryKey.length > 0) {
            return { ai, primaryKey };
        }
    }
    const wsMatch = /^(.+)_(01|8006|8013|8010|414|415|417|8017|8018|255|00|253|401|402|8003|8004)_([^_]+)$/.exec(docId);
    if (wsMatch !== null) {
        const workspaceId = wsMatch[1];
        const ai = wsMatch[2];
        const primaryKey = wsMatch[3];
        if (workspaceId !== undefined &&
            ai !== undefined &&
            primaryKey !== undefined &&
            primaryKey.length > 0) {
            return { workspaceId, ai, primaryKey };
        }
    }
    return null;
}
export const GS1_PRIMARY_KEY_OPTIONS = [
    {
        code: GS1_AI.GTIN,
        keyName: 'GTIN',
        label: `GTIN (${GS1_AI.GTIN}) — Global Trade Item Number`,
        placeholder: '09506000134352',
    },
    {
        code: GS1_AI.ITIP,
        keyName: 'ITIP',
        label: `ITIP (${GS1_AI.ITIP}) — Identification of Trade Item Pieces`,
        placeholder: '095060001343520102',
    },
    {
        code: GS1_AI.GMN,
        keyName: 'GMN',
        label: `GMN (${GS1_AI.GMN}) — Global Model Number`,
        placeholder: '9506000MODEL123XK',
    },
    {
        code: GS1_AI.CPID,
        keyName: 'CPID',
        label: `CPID (${GS1_AI.CPID}) — Component / Part Identifier`,
        placeholder: '9506000PART99',
    },
    {
        code: GS1_AI.PHYSICAL_GLN,
        keyName: 'GLN (Physical)',
        label: `GLN (${GS1_AI.PHYSICAL_GLN}) — Physical Location GLN`,
        placeholder: '9506000134352',
    },
    {
        code: GS1_AI.PAY_TO_GLN,
        keyName: 'GLN (Invoicing)',
        label: `GLN (${GS1_AI.PAY_TO_GLN}) — Invoicing Party GLN`,
        placeholder: '9506000134352',
    },
    {
        code: GS1_AI.PARTY_GLN,
        keyName: 'GLN (Party)',
        label: `GLN (${GS1_AI.PARTY_GLN}) — Party GLN`,
        placeholder: '9506000134352',
    },
    {
        code: GS1_AI.GSRN_PROVIDER,
        keyName: 'GSRN (Provider)',
        label: `GSRN (${GS1_AI.GSRN_PROVIDER}) — Service Provider GSRN`,
        placeholder: '950600013435200010',
    },
    {
        code: GS1_AI.GSRN_RECIPIENT,
        keyName: 'GSRN (Recipient)',
        label: `GSRN (${GS1_AI.GSRN_RECIPIENT}) — Service Recipient GSRN`,
        placeholder: '950600013435200010',
    },
    {
        code: GS1_AI.GCN,
        keyName: 'GCN',
        label: `GCN (${GS1_AI.GCN}) — Global Coupon Number`,
        placeholder: '9506000134352',
    },
    {
        code: GS1_AI.SSCC,
        keyName: 'SSCC',
        label: `SSCC (${GS1_AI.SSCC}) — Serial Shipping Container Code`,
        placeholder: '195060001343520009',
    },
    {
        code: GS1_AI.GDTI,
        keyName: 'GDTI',
        label: `GDTI (${GS1_AI.GDTI}) — Global Document Type Identifier`,
        placeholder: '9506000134352',
    },
    {
        code: GS1_AI.GINC,
        keyName: 'GINC',
        label: `GINC (${GS1_AI.GINC}) — Global Identification Number for Consignment`,
        placeholder: '9506000CONSIGN01',
    },
    {
        code: GS1_AI.GSIN,
        keyName: 'GSIN',
        label: `GSIN (${GS1_AI.GSIN}) — Global Shipment Identification Number`,
        placeholder: '95060001343520007',
    },
    {
        code: GS1_AI.GRAI,
        keyName: 'GRAI',
        label: `GRAI (${GS1_AI.GRAI}) — Global Returnable Asset Identifier`,
        placeholder: '09506000134352',
    },
    {
        code: GS1_AI.GIAI,
        keyName: 'GIAI',
        label: `GIAI (${GS1_AI.GIAI}) — Global Individual Asset Identifier`,
        placeholder: '9506000ASSET001',
    },
];
const CONFORMANT_KEY_TYPE_TO_AI = {
    '01': GS1_AI.GTIN,
    gtin: GS1_AI.GTIN,
    '8006': GS1_AI.ITIP,
    itip: GS1_AI.ITIP,
    '8013': GS1_AI.GMN,
    gmn: GS1_AI.GMN,
    '8010': GS1_AI.CPID,
    cpid: GS1_AI.CPID,
    '414': GS1_AI.PHYSICAL_GLN,
    gln: GS1_AI.PHYSICAL_GLN,
    '415': GS1_AI.PAY_TO_GLN,
    payto: GS1_AI.PAY_TO_GLN,
    '417': GS1_AI.PARTY_GLN,
    partygln: GS1_AI.PARTY_GLN,
    '8017': GS1_AI.GSRN_PROVIDER,
    gsrnp: GS1_AI.GSRN_PROVIDER,
    '8018': GS1_AI.GSRN_RECIPIENT,
    gsrn: GS1_AI.GSRN_RECIPIENT,
    '255': GS1_AI.GCN,
    gcn: GS1_AI.GCN,
    '00': GS1_AI.SSCC,
    sscc: GS1_AI.SSCC,
    '253': GS1_AI.GDTI,
    gdti: GS1_AI.GDTI,
    '401': GS1_AI.GINC,
    ginc: GS1_AI.GINC,
    '402': GS1_AI.GSIN,
    gsin: GS1_AI.GSIN,
    '8003': GS1_AI.GRAI,
    grai: GS1_AI.GRAI,
    '8004': GS1_AI.GIAI,
    giai: GS1_AI.GIAI,
};
/**
 * Resolves a GS1-Conformant Resolver Standard v1.2.1 §2.6 `identificationKeyType`
 * (either numeric AI code or short name like `gtin`, `gln`, `sscc`) into a canonical `PrimaryKeyAI`.
 */
export function resolveConformantKeyTypeToAi(keyType) {
    const normalized = keyType.trim().toLowerCase();
    return CONFORMANT_KEY_TYPE_TO_AI[normalized] ?? null;
}
//# sourceMappingURL=keys.js.map